// Drop-zone + file list with AI categorization + user verification.
//
// UX:
//   - Drop area takes any file. Multi-select, drag-drop, or click-to-pick.
//   - Each upload streams progress via the mutation state.
//   - Once registered, the row appears with "Categorizing…" until AI lands.
//   - User confirms (✓) or corrects (dropdown) — confirmation timestamps the row.

import type { FileCategoryValue } from '@renovation-claim/schemas';
import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../api';
import {
  type CaseFile,
  useConfirmFile,
  useDeleteFile,
  useFiles,
  useRetryFileExtraction,
  useSources,
  useUpdateFileCategory,
  useUploadFile,
} from '../files';
import { m } from '../paraglide/messages';
import { useInvalidateProgress } from '../progress';
import { useInvalidateTimeline } from '../timeline';

const CATEGORY_OPTIONS: FileCategoryValue[] = [
  'contract',
  'invoice',
  'receipt',
  'photo',
  'whatsapp_export',
  'email',
  'other',
];

function categoryLabel(c: FileCategoryValue): string {
  switch (c) {
    case 'contract':
      return m.fileCategoryContract();
    case 'invoice':
      return m.fileCategoryInvoice();
    case 'receipt':
      return m.fileCategoryReceipt();
    case 'photo':
      return m.fileCategoryPhoto();
    case 'whatsapp_export':
      return m.fileCategoryWhatsAppExport();
    case 'email':
      return m.fileCategoryEmail();
    case 'other':
      return m.fileCategoryOther();
  }
}

const MAX_BYTES = 25 * 1024 * 1024;

export function FilesPanel({ caseId }: { caseId: string }) {
  const files = useFiles(caseId);
  const upload = useUploadFile(caseId);
  const invalidateTimeline = useInvalidateTimeline();
  const invalidateProgress = useInvalidateProgress();
  const [error, setError] = useState<string | null>(null);

  // When extraction adds events to the timeline, the timeline + progress
  // queries need to refetch. Drive this off the total extracted count so
  // we invalidate exactly once per "wave" of new events.
  //
  // CRITICAL: only invalidate on an INCREASE from a previously-known
  // baseline. The baseline is captured the first time the files query
  // actually returns data — NOT on the mount-with-no-data render, where
  // `totalExtracted` is the placeholder 0 from `files.data ?? []`. Without
  // this guard, switching to the Sources tab made the count go from
  // 0 (placeholder) → N (loaded), which counted as "extraction added
  // events" and re-fired both invalidations. That cascaded into
  // useAutoRecheckConsistency and made the chronology badge flicker
  // through "Re-checking…" every time the user clicked Sources.
  const totalExtracted = (files.data ?? []).reduce((n, f) => n + f.extracted_event_count, 0);
  const prevExtractedRef = useRef<number | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: invalidate fns are stable react-query refs
  useEffect(() => {
    if (!files.data) return; // wait for first real load before establishing a baseline
    const prev = prevExtractedRef.current;
    prevExtractedRef.current = totalExtracted;
    // Skip the very first real load (the baseline). Only invalidate when
    // the count grew compared to a baseline we've already observed.
    if (prev !== null && totalExtracted > prev) {
      invalidateTimeline(caseId);
      invalidateProgress(caseId);
    }
  }, [caseId, totalExtracted, files.data]);

  const handleFiles = async (list: FileList | File[]) => {
    setError(null);
    const arr = Array.from(list);
    for (const f of arr) {
      if (f.size > MAX_BYTES) {
        setError(m.uploadTooLarge({ filename: f.name }));
        continue;
      }
      try {
        await upload.mutateAsync(f);
      } catch (e) {
        setError(e instanceof Error ? e.message : m.uploadFailed());
      }
    }
  };

  return (
    <div className="files-panel">
      <h3 className="sources-section-head">{m.sourcesTabFiles()}</h3>
      <DropZone disabled={upload.isPending} onFiles={handleFiles} />
      {error ? <div className="notice error">{error}</div> : null}
      {files.isLoading ? (
        <p className="muted small">{m.filesLoading()}</p>
      ) : (files.data ?? []).length === 0 ? (
        <p className="muted small empty-files">{m.filesEmpty()}</p>
      ) : (
        <ul className="files-list">
          {(files.data ?? []).map((f) => (
            <FileRow key={f.id} caseId={caseId} file={f} />
          ))}
        </ul>
      )}
      <FeedbackSection caseId={caseId} />
    </div>
  );
}

/**
 * The Sources tab also lists the user's chat messages that grounded a
 * timeline event — those are sources of truth too. Rendered as a thin
 * subsection below the file list. Empty by default; appears once the
 * user starts adding facts via chat.
 */
function FeedbackSection({ caseId }: { caseId: string }) {
  const sources = useSources(caseId);
  const items = sources.data?.feedback ?? [];
  if (items.length === 0 && !sources.isLoading) {
    return (
      <section className="sources-feedback">
        <h3 className="sources-section-head">{m.sourcesTabFeedback()}</h3>
        <p className="muted small">{m.sourcesEmptyFeedback()}</p>
      </section>
    );
  }
  return (
    <section className="sources-feedback">
      <h3 className="sources-section-head">{m.sourcesTabFeedback()}</h3>
      <ul className="sources-feedback-list">
        {items.map((item) => (
          <li key={item.message_id} className="sources-feedback-row">
            <p className="sources-feedback-snippet">{item.snippet || '—'}</p>
            <ul className="sources-feedback-events">
              {item.shapedEvents.map((e) => (
                <li key={e.id} className="sources-feedback-event">
                  <span className="sources-feedback-event-date">{e.date}</span>{' '}
                  <span className="sources-feedback-event-summary">{e.summary}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DropZone({
  onFiles,
  disabled,
}: {
  onFiles: (files: FileList) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOver, setIsOver] = useState(false);

  return (
    // Drag-and-drop zone. A native <button> won't accept dropped files (HTML
    // forbids interactive content from being a drop target), so we keep a
    // <div role="button"> with explicit keyboard + tabIndex handling.
    <div
      className={`drop-zone ${isOver ? 'drop-zone-over' : ''} ${disabled ? 'drop-zone-disabled' : ''}`}
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsOver(false);
        if (!disabled && e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
      }}
      // biome-ignore lint/a11y/useSemanticElements: dropzone needs to accept dropped files; <button> can't
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          inputRef.current?.click();
        }
      }}
    >
      <p className="drop-zone-title">{disabled ? m.filesDropUploading() : m.filesDropTitle()}</p>
      <p className="muted small">{m.filesDropSubtitle()}</p>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) onFiles(e.target.files);
          // Reset so picking the same file twice fires onChange
          e.target.value = '';
        }}
      />
    </div>
  );
}

function FileRow({
  caseId,
  file,
}: {
  caseId: string;
  file: CaseFile;
}) {
  const updateCat = useUpdateFileCategory(caseId);
  const confirm = useConfirmFile(caseId);
  const del = useDeleteFile(caseId);
  const retry = useRetryFileExtraction(caseId);

  const isCategorizing = file.ai_category === null;
  const isConfirmed = file.user_confirmed_at !== null;
  const confidence = file.ai_confidence ? Number(file.ai_confidence) : null;
  const lowConfidence = confidence !== null && confidence < 0.6;
  const isExtracting = file.extraction_status === 'extracting';
  const extractionFailed = file.extraction_status === 'failed';

  return (
    <li className="file-row">
      <div className="file-info">
        <div className="file-name">
          <FileOpenLink caseId={caseId} file={file} />
        </div>
        {/* The size + content-type metadata that used to live here was
         * cargo from a generic file-manager mental model. In a case
         * workspace the user already knows what they uploaded; the
         * filename + extension carry the type, and bytes don't help
         * decide whether to keep, recategorise, or delete. We keep the
         * extraction status inline because *that* is actionable. */}
        {isExtracting || extractionFailed ? (
          <div className="file-meta small muted">
            {isExtracting ? <span className="file-meta-status">{m.filesExtracting()}</span> : null}
            {extractionFailed ? (
              <span className="file-meta-status file-meta-status-err">
                {m.filesExtractionFailedShort()}
                <button
                  type="button"
                  className="btn-small btn-ghost file-extraction-retry"
                  onClick={() => retry.mutate(file.id)}
                  disabled={retry.isPending}
                  title={m.filesRetryTitle()}
                >
                  {retry.isPending ? m.filesRetrying() : m.filesRetry()}
                </button>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="file-controls">
        {isCategorizing ? (
          <span className="muted small">{m.filesCategorizing()}</span>
        ) : (
          <>
            <select
              className="file-cat"
              value={file.category ?? 'other'}
              onChange={(e) =>
                updateCat.mutate({
                  fileId: file.id,
                  category: e.target.value as FileCategoryValue,
                })
              }
              disabled={updateCat.isPending}
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {categoryLabel(c)}
                </option>
              ))}
            </select>
            {!isConfirmed ? (
              <button
                type="button"
                className={lowConfidence ? 'btn-small btn-warn' : 'btn-small'}
                onClick={() => confirm.mutate(file.id)}
                disabled={confirm.isPending}
                title={
                  lowConfidence
                    ? m.filesConfirmLowConfidence({
                        percent: (confidence! * 100).toFixed(0),
                      })
                    : m.filesConfirmTitle()
                }
              >
                {m.filesConfirm()}
              </button>
            ) : (
              <span className="file-confirmed muted small">{m.filesConfirmed()}</span>
            )}
            <button
              type="button"
              className="btn-small btn-ghost"
              onClick={() => {
                if (window.confirm(m.filesDeleteConfirm({ filename: file.filename })))
                  del.mutate(file.id);
              }}
              disabled={del.isPending}
              title={m.filesDelete()}
              aria-label={m.filesDelete()}
            >
              ✕
            </button>
          </>
        )}
      </div>
    </li>
  );
}

// The filename is now the open affordance — the user uploaded a file,
// they should be able to view it. We mint the signed URL on demand
// (5-minute TTL) and open it in a new tab. Failure is non-fatal: the
// link reverts to its idle state and shows an inline error chip.
function FileOpenLink({ caseId, file }: { caseId: string; file: CaseFile }) {
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  async function open() {
    if (state === 'loading') return;
    setState('loading');
    try {
      const res = await apiFetch<{ url: string }>(`/cases/${caseId}/files/${file.id}/view-url`);
      window.open(res.url, '_blank', 'noopener,noreferrer');
      setState('idle');
    } catch {
      setState('error');
      window.setTimeout(() => setState('idle'), 3000);
    }
  }
  return (
    <>
      <button
        type="button"
        className="link-button file-open-link"
        onClick={open}
        title={m.filesOpenTitle()}
        disabled={state === 'loading'}
      >
        {file.filename}
      </button>
      {state === 'error' ? (
        <span className="small file-extraction-err"> · {m.filesOpenError()}</span>
      ) : null}
    </>
  );
}
